export const LEGAL_CONTENT = {
  TITLE: 'Legal Information',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  INTRO: [
    'This section of our website contains important and relevant information related to our practices, policies, and legal obligations. By accessing this area, you can find details about our regulatory status, complaint procedures, and other essential documentation.',
    'For secure and compliant browsing, we recommend reviewing these documents regularly. If you have any questions or need further information about our legal documents, please contact our compliance team.'
  ],
  DOCUMENTS: [
    {
      TITLE: 'Securitization in Luxembourg',
      DESCRIPTION:
        'Information and regulatory details regarding securitization practices and legal framework in Luxembourg.',
      IS_PDF: true,
      PDF_ID: 'securitization-luxembourg'
    },
    {
      TITLE: 'Important Disclosures',
      DESCRIPTION:
        'Relevant information about our services, including warnings and legal considerations that all users should be aware of.',
      IS_PDF: false,
      PATH: '/legal/important-disclosures'
    },
    {
      TITLE: 'Tax Strategy',
      DESCRIPTION:
        'Our approach to tax compliance for the financial year ending November 2025, in compliance with Section 17, Paragraph 4 of Schedule 19 to the Finance Act 2016.',
      IS_PDF: false,
      PATH: '/legal/tax-strategy'
    },
    {
      TITLE: 'Modern Slavery Statement',
      DESCRIPTION:
        'Our commitment to eradicating any form of modern slavery within our business operations, as required by applicable legislation.',
      IS_PDF: false,
      PATH: '/legal/modern-slavery-statement'
    },
    {
      TITLE: 'Terms and Conditions of Use',
      DESCRIPTION:
        'The terms and conditions governing the use of our website and services, including the rights and responsibilities of users.',
      IS_PDF: false,
      PATH: '/legal/terms-and-conditions'
    },
    {
      TITLE: 'Recruitment Policy Notice',
      DESCRIPTION:
        'The guidelines and practices we follow during the recruitment process to ensure equal opportunities and compliance with applicable labor laws.',
      IS_PDF: false,
      PATH: '/legal/recruitment-policy-notice'
    },
    {
      TITLE: 'Online Privacy Statement',
      DESCRIPTION:
        'Details on how we collect, use, and protect personal information of our users when interacting with our website and services.',
      IS_PDF: false,
      PATH: '/legal/online-privacy-statement'
    },
    {
      TITLE: 'Cookie Policy',
      DESCRIPTION:
        'Information about the cookies used on our website, their purpose, and how users can control them.',
      IS_PDF: false,
      PATH: '/legal/cookie-policy'
    }
  ],
  FOOTER_NOTE:
    'MarQ Capital is committed to maintaining the highest standards of legal compliance and ethical practices in all aspects of our business.'
};

export const COOKIE_POLICY = {
  TITLE: 'Cookie Policy',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  INTRO:
    'At MarQ Capital, we respect your privacy and are committed to protecting your personal information. This Cookie Policy explains how we use cookies on our website, the types of cookies we use, and how you can manage your cookie preferences.',
  SECTIONS: {
    WHAT_ARE_COOKIES: {
      TITLE: 'What are cookies?',
      PARAGRAPHS: [
        'Cookies are small text files that are stored on your device (computer, tablet, smartphone) when you visit a website. Cookies allow the website to remember your preferences, past activities, and customize your browsing experience.'
      ]
    },
    COOKIE_TYPES: {
      TITLE: 'Types of cookies we use',
      LIST_ITEMS: [
        {
          TITLE: 'Strictly Necessary Cookies',
          TEXT: 'These cookies are essential for enabling basic functionality, such as accessing secure areas and submitting forms. Without these cookies, some services may not be available.'
        },
        {
          TITLE: 'Performance Cookies',
          TEXT: 'These cookies collect information about how visitors use our website, such as the most visited pages and any error messages. These cookies do not collect personally identifiable information and are used exclusively to improve the performance of the website.'
        },
        {
          TITLE: 'Functionality Cookies',
          TEXT: 'These cookies allow the website to remember your choices, such as your username, language, or location, and provide enhanced, personalized features.'
        },
        {
          TITLE: 'Advertising and Marketing Cookies',
          TEXT: 'These cookies are used to display personalized ads based on your interests. They are also used to limit the number of times you see an advertisement and to measure the effectiveness of advertising campaigns.'
        },
        {
          TITLE: 'Third-Party Cookies',
          TEXT: 'We may also use third-party cookies, such as social media and analytics tools (e.g., Google Analytics), to gather information about user behavior and improve our services. These cookies may collect data about your browsing across multiple websites.'
        }
      ]
    },
    HOW_WE_USE: {
      TITLE: 'How we use cookies',
      INTRO: 'We use cookies for the following purposes:',
      LIST_ITEMS: [
        { TEXT: 'To enhance the user experience on our website.' },
        { TEXT: 'To personalize content and ads.' },
        { TEXT: 'To monitor website usage and performance.' },
        { TEXT: 'To conduct data analysis to improve our services.' },
        { TEXT: 'To engage in marketing and advertising activities.' }
      ]
    },
    MANAGING_COOKIES: {
      TITLE: 'Managing cookies',
      INTRO:
        'You have the right to control and manage the cookies stored on your device. You can do this in several ways:',
      LIST_ITEMS: [
        {
          TITLE: 'Browser Settings',
          TEXT: 'Most browsers allow you to refuse or accept cookies. You can configure your browser to notify you whenever a cookie is sent or block specific cookies. If you block cookies, some features of the website may not work properly.'
        },
        {
          TITLE: 'Cookie Settings on Our Website',
          TEXT: 'When you first visit our website, a notification will ask for your consent to use cookies. You can accept or reject cookies, as well as customize your preferences through the cookie bar or on the cookie settings page.'
        },
        {
          TITLE: 'Third-Party Tools',
          TEXT: 'Some cookies, such as advertising cookies, can be controlled through third-party tools. You can access these tools to manage your cookie preferences.'
        }
      ]
    },
    THIRD_PARTY: {
      TITLE: 'Third-Party Cookies',
      PARAGRAPHS: [
        'We may use third-party services, such as Google Analytics, to analyze user behavior on our website. These services may use cookies to collect information about your visit and help improve the functionality and content of our website.',
        'For more information about Google Analytics cookies, you can review the Google Privacy Policy.'
      ],
      LINKS: [
        {
          TEXT: 'Google Privacy Policy',
          URL: 'https://policies.google.com/privacy'
        }
      ]
    },
    DATA_RETENTION: {
      TITLE: 'Data Retention',
      PARAGRAPHS: [
        'The cookies we use are stored on your device for as long as necessary to fulfill the purposes for which they were set, after which they will be automatically deleted, except for persistent cookies, which remain stored until you delete them.'
      ]
    },
    POLICY_CHANGES: {
      TITLE: 'Changes to this Cookie Policy',
      PARAGRAPHS: [
        'We may update this Cookie Policy from time to time to reflect changes in our services or applicable laws. When this occurs, we will post the new version on our website and indicate the date of the last update. We encourage you to review this policy regularly to stay informed about our use of cookies.'
      ]
    },
    CONTACT_US: {
      TITLE: 'Contact Us',
      PARAGRAPHS: [
        'If you have any questions about this Cookie Policy or how we use cookies, please contact us at:',
        'MarQ Capital Limited\\nLevel 30, The Leadenhall Building\\n122 Leadenhall Street\\nLondon EC3V 4AB UK\\n compliance@mqcapital.co.uk\\n+44 20 4587 3860',
        'This Cookie Policy ensures that users have a clear and detailed understanding of how cookies are used on your site and provides them with the ability to manage their preferences transparently.'
      ],
      LINKS: [
        {
          TEXT: 'compliance@mqcapital.co.uk',
          URL: 'mailto:compliance@mqcapital.co.uk'
        }
      ]
    }
  },
  BREADCRUMBS: [
    { TEXT: 'Home', URL: '/' },
    { TEXT: 'Legal', URL: '/legal' },
    {
      TEXT: 'Cookie Policy',
      URL: '/legal/cookie-policy'
    }
  ],
  FOOTER_NOTE:
    'Your privacy is important to us. We are committed to being transparent about our use of cookies.'
};

export const IMPORTANT_DISCLOSURES = {
  TITLE: 'Important Disclosures',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  INTRO:
    'The information provided by MarQ Capital, including content on our website, social media profiles, and third-party platforms linked to it, is for informational purposes only. This content does not constitute investment advice and should not be considered as an offer or solicitation for any financial asset or product.',
  SECTIONS: {
    NO_GUARANTEE: {
      TITLE: 'No Guarantee of Performance',
      PARAGRAPHS: [
        'There is no guarantee that the goals, expectations, or strategies mentioned in our materials will be or have been achieved, nor that the activities will continue as described. Past performance is not indicative of future results, and the performance of investments can vary based on market conditions and other external factors.'
      ]
    },
    LIABILITY: {
      TITLE: 'Liability Disclaimer',
      PARAGRAPHS: [
        'MarQ Capital, its affiliates, and any person or entity associated directly or indirectly with it, including those acting on its behalf, shall not be held liable for any errors in the content, whether due to negligence or otherwise, to the fullest extent permitted by law (except in cases of fraud). We are not responsible for the consequences of third parties relying on such content, and users are encouraged to seek independent advice before making investment decisions.'
      ]
    },
    INTELLECTUAL_PROPERTY: {
      TITLE: 'Intellectual Property',
      PARAGRAPHS: [
        'All brands, logos, trademarks, and information published by MarQ Capital on its website and related platforms are owned by the company or used with permission. These materials may not be reproduced, distributed, or retransmitted without prior written consent from MarQ Capital.'
      ]
    },
    RECORDING_AND_DATA: {
      TITLE: 'Recording of Calls and Collection of Personal Information',
      PARAGRAPHS: [
        'MarQ Capital (London), may record telephone calls made to or from your business. Additionally, personal information shared during business interactions may be collected and processed by us. This information is used for service provision, record-keeping, processing job applications, regulatory compliance, and other related purposes. As necessary, such information may be transferred outside the European Economic Area (EEA) to other companies within the MarQ Capital group.'
      ]
    }
  },
  BREADCRUMBS: [
    { TEXT: 'Home', URL: '/' },
    { TEXT: 'Legal', URL: '/legal' },
    {
      TEXT: 'Important Disclosures',
      URL: '/legal/important-disclosures'
    }
  ],
  FOOTER_NOTE:
    'This document contains important legal information regarding our services. Please review it carefully.'
};

export const TAX_STRATEGY = {
  TITLE: 'Tax Strategy',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  INTRO: [
    'MarQ Capital is a Quantitative Strategy Provider company registered in England and Wales. This tax strategy applies to the financial year ending on 30 November 2025 and complies with the requirements outlined in Section 17, Paragraph 4 of Schedule 19 to the Finance Act 2016.',
    'MarQ Capital is fully committed to complying with all relevant tax legislation, regulations, and reporting obligations in the United Kingdom and in all jurisdictions where we operate. We adopt a transparent approach to tax matters, ensuring that we do not engage in aggressive tax planning or tax avoidance schemes. Our tax strategy is aligned with our commitment to integrity, transparency, and corporate social responsibility.'
  ],
  SECTIONS: {
    RISK_MANAGEMENT: {
      TITLE: 'How MarQ Capital Manages Fiscal Risk',
      LIST_ITEMS: [
        {
          TITLE: 'Strong Governance Framework',
          TEXT: 'At MarQ Capital, we are committed to strong governance in managing, identifying, and communicating fiscal risks. This includes oversight at the highest levels, with clear roles for the Risk Committee and Board of Directors in assessing and monitoring tax risks.'
        },
        {
          TITLE: 'Identification and Monitoring of Risks',
          TEXT: 'As our business evolves and the legislative and regulatory landscape continues to change, we proactively identify new tax risks. This is achieved by following legal and political developments, as well as assessing the fiscal impact of potential internal changes.'
        },
        {
          TITLE: 'Day-to-Day Responsibility',
          TEXT: 'The primary responsibility for ensuring compliance with all applicable tax laws lies with our Tax Director, who manages daily operations and coordinates compliance efforts across the business. However, the Board of Directors and Risk Committee provide oversight and input on our overall tax approach and positions.'
        },
        {
          TITLE: 'Consulting External Experts',
          TEXT: "For complex or uncertain tax matters, we engage with external consultants to seek professional guidance. If necessary, we will also request second opinions to mitigate potential risks or directly approach HMRC (His Majesty's Revenue and Customs) for clarification on specific issues."
        },
        {
          TITLE: 'Continuous Improvement',
          TEXT: 'We regularly review and update our tax policies, processes, and systems to ensure ongoing compliance and effectiveness in mitigating risks.'
        }
      ]
    },
    TAX_PLANNING: {
      TITLE: 'Our Approach to Tax Planning',
      LIST_ITEMS: [
        {
          TITLE: 'Commitment to Compliance',
          TEXT: 'MarQ Capital is dedicated to fulfilling all tax-related obligations. We view tax compliance not only as a legal requirement but also as a fundamental element of our corporate social responsibility. We take pride in our ethical approach to managing tax matters.'
        },
        {
          TITLE: 'Clear Tax Calculation Processes',
          TEXT: 'We have established well-defined processes to ensure taxes are accurately calculated, paid promptly, and the risk of error is minimised. These processes are regularly reviewed and monitored to ensure compliance with current regulations.'
        },
        {
          TITLE: 'Maximising Benefits Legally',
          TEXT: 'While our focus remains on compliance, we will also seek to access any available tax incentives, benefits, and exemptions in a lawful and transparent manner.'
        },
        {
          TITLE: 'Economic Substance in Trade Structuring',
          TEXT: 'When structuring trade agreements, we consider taxes as one factor among others. However, all transactions will have real economic substance and will not be structured with artificial tax elements. Our approach to tax planning is aligned with the broader business objectives and sustainable growth of the company.'
        }
      ]
    },
    RISK_TOLERANCE: {
      TITLE: 'Fiscal Risk Tolerance',
      LIST_ITEMS: [
        {
          TITLE: 'Low-Risk Approach',
          TEXT: 'MarQ Capital adopts a conservative, low-risk approach to tax matters. We have implemented robust procedures for managing fiscal risks and work with trusted legal, tax, and compliance professionals to ensure we stay informed of any changes in relevant tax regulations.'
        },
        {
          TITLE: 'Collaboration with Tax Advisors',
          TEXT: 'We maintain strong relationships with reputable tax advisors and legal firms in the jurisdictions where we operate. These partnerships ensure that we are continuously updated on any changes to tax legislation, both in the UK and internationally.'
        },
        {
          TITLE: 'Review and Verification of Tax Calculations',
          TEXT: 'All tax calculations are thoroughly reviewed both internally and by external third parties to ensure accuracy and compliance with applicable laws. This reduces the likelihood of errors and misinterpretation of tax regulations.'
        }
      ]
    },
    HMRC_RELATIONSHIP: {
      TITLE: 'Relationship with HMRC',
      LIST_ITEMS: [
        {
          TITLE: 'Open Dialogue',
          TEXT: 'MarQ Capital is committed to maintaining a transparent and constructive relationship with HMRC. We engage in regular discussions regarding our understanding of tax legislation, case law, and any guidance issued by HMRC to ensure our compliance is aligned with regulatory expectations.'
        },
        {
          TITLE: 'Proactive Engagement',
          TEXT: 'We maintain an open channel of communication with HMRC, especially in cases of complex tax matters, to ensure clarity on any areas of uncertainty and to resolve any issues promptly.'
        }
      ]
    }
  },
  FOOTER_NOTE:
    'This document outlines our approach to tax compliance and risk management in accordance with UK legislation.'
};

export const MODERN_SLAVERY = {
  TITLE: 'Modern Slavery Statement',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  INTRO:
    "This statement is made pursuant to section 54 of the Modern Slavery Act 2015 (the 'Act') and outlines the steps MarQ Capital has taken and will continue to take to prevent modern slavery and human trafficking within our business and supply chain. Modern slavery includes slavery, servitude, forced or compulsory labour, and human trafficking.",
  SECTIONS: {
    COMMITMENT: {
      TITLE: 'Our Commitment',
      PARAGRAPHS: [
        'At MarQ Capital, we are committed to conducting our business in an ethical and responsible manner. This includes ensuring that all relationships with our stakeholders, clients, partners, employees, shareholders, and suppliers, adhere to the highest standards. We will not tolerate modern slavery in any part of our business or supply chain and are committed to identifying and mitigating the risks of modern slavery.'
      ]
    },
    BUSINESS: {
      TITLE: 'Our Business',
      PARAGRAPHS: [
        'In MarQ Capital we are dedicated to ensuring the integrity of our operations and supply chain, promoting responsible investment practices, and upholding the principles of the United Nations supported Principles for Responsible Investment (PRI).',
        'Our approach integrates environmental, social, and governance (ESG) factors, including the risk of modern slavery, into our Quantitative Strategy process.'
      ]
    },
    RISK: {
      TITLE: 'Risk of Modern Slavery and Human Trafficking',
      PARAGRAPHS: [
        'Given the professional nature of our business, MarQ Capital operates with a relatively low risk of modern slavery or human trafficking in its direct operations. Our supply chains are composed mainly of regulated financial service providers, professional service firms, and other reputable entities, which reduces the likelihood of encountering modern slavery-related risks.',
        'While we operate predominantly within the financial services sector, we remain vigilant and continuously evaluate the potential risks that may arise in our supply chain.'
      ]
    },
    STEPS: {
      TITLE: 'Steps Taken to Combat Modern Slavery',
      INTRO:
        'MarQ Capital takes a risk-based approach to assess and manage the potential risks of modern slavery. We have implemented various measures to mitigate such risks, including:',
      LIST_ITEMS: [
        {
          TITLE: 'Due Diligence',
          TEXT: 'We conduct thorough due diligence on new suppliers and expect them to comply with relevant legislation in all jurisdictions where they operate, including the UK. We pay particular attention to suppliers and partners operating in high-risk regions.'
        },
        {
          TITLE: 'Whistleblowing Policy',
          TEXT: 'Our Whistleblowing Policy provides employees with a confidential channel to report any concerns related to modern slavery or unethical behaviour without fear of retaliation.'
        },
        {
          TITLE: 'Employee Awareness',
          TEXT: 'All employees are trained annually on modern slavery issues, ensuring they are aware of the signs and implications of modern slavery and how to report concerns.'
        },
        {
          TITLE: 'Recruitment and Onboarding',
          TEXT: 'We have a robust recruitment process in place to verify that all employees have the right to work in the UK, are paid fairly, and are not subject to any form of forced labour or exploitation.'
        },
        {
          TITLE: 'Supplier Agreements',
          TEXT: 'We require that all suppliers and contractors adhere to our ethical standards and comply with applicable laws regarding modern slavery. Our contracts include clauses that require suppliers to confirm their commitment to anti-slavery and anti-human trafficking practices.'
        },
        {
          TITLE: 'Ongoing Review',
          TEXT: 'We review the risks of modern slavery annually as part of our overall risk management framework, ensuring that we remain proactive in identifying and mitigating any potential risks in our operations or supply chain.'
        }
      ]
    },
    POLICIES: {
      TITLE: 'Policies and Procedures',
      LIST_ITEMS: [
        {
          TITLE: 'Equal Opportunities Policy',
          TEXT: 'We uphold the principles of equal opportunity and ensure that all employees are treated fairly and without discrimination. We oppose all forms of discrimination, harassment, or victimisation.'
        },
        {
          TITLE: 'Bullying and Harassment Policy',
          TEXT: 'We have a comprehensive policy to address and prevent bullying and harassment, ensuring a safe and respectful work environment for all employees.'
        },
        {
          TITLE: 'Employee Assistance Programme',
          TEXT: 'We provide employees with access to confidential counselling services via our Employee Assistance Programme (EAP), where they can seek independent advice and support if needed.'
        }
      ]
    },
    CONCLUSION: {
      TITLE: 'Conclusion',
      PARAGRAPHS: [
        'At MarQ Capital, we are committed to maintaining the highest ethical standards in all aspects of our operations. This Modern Slavery Statement is reviewed and updated annually to reflect any changes in our policies or business practices. Our Board of Directors has approved this statement, and we remain committed to ensuring that modern slavery and human trafficking are not present in any part of our supply chain or operations.',
        'This statement was reviewed by our senior management team and approved by the Board of Directors of MarQ Capital on May 1, 2025.'
      ]
    }
  },
  FOOTER_NOTE:
    'We are committed to conducting our business ethically and responsibly to prevent modern slavery and human trafficking.'
};

export const TERMS_AND_CONDITIONS = {
  TITLE: 'Terms and Conditions of Use',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  INTRO:
    'By using our website, you confirm that you accept these terms of service and agree to comply with them. If you do not agree to these terms, you must not use our site. We recommend that you print a copy of these terms for future reference.',
  SECTIONS: {
    ADDITIONAL_TERMS: {
      TITLE: 'Additional Terms',
      PARAGRAPHS: [
        'These Terms of Service refer to the following additional terms, which also apply to your use of our site:'
      ],
      LIST_ITEMS: [
        {
          TITLE: 'Our Online Privacy Statement',
          TEXT: 'Which explains how we collect, use, and store your personal data.'
        },
        {
          TITLE: 'Our Cookie Policy',
          TEXT: 'Which outlines information about the cookies on our site.'
        }
      ],
      ADDITIONAL_PARAGRAPH:
        'If you purchase services from us, the terms set out in our relevant Terms of Business provided prior to the supply of such services will apply.'
    },
    CHANGES_TO_TERMS: {
      TITLE: 'Changes to These Terms',
      PARAGRAPHS: [
        'We amend these terms from time to time. Every time you wish to use our site, please check these terms to ensure you understand the terms that apply at that time. These terms were most recently updated on May 1, 2025.'
      ]
    },
    CHANGES_TO_SITE: {
      TITLE: 'Changes to Our Site',
      PARAGRAPHS: [
        'We may update and change our site periodically, including to reflect changes to our products and services, user needs, and business priorities.'
      ]
    },
    SUSPENSION: {
      TITLE: 'Suspension or Withdrawal of Our Site',
      PARAGRAPHS: [
        'Our site is made available free of charge. We do not guarantee that our site, or any content on it, will always be available or uninterrupted. We may suspend, withdraw, or restrict the availability of all or any part of our site, including for business and operational reasons.',
        'You are also responsible for ensuring that all persons who access our site through your internet connection are aware of these terms of service and other applicable terms, and that they comply with them.'
      ]
    },
    TRANSFER_OF_RIGHTS: {
      TITLE: 'Transfer of Rights and Obligations',
      PARAGRAPHS: [
        'We may transfer our rights and obligations under these terms to another organization. We will notify you in writing if this happens and ensure that the transfer will not affect your rights under the contract.'
      ]
    },
    GEOGRAPHIC_LIMITATIONS: {
      TITLE: 'Geographic Limitations',
      PARAGRAPHS: [
        'Our site is directed to users residing in the United Kingdom. We do not represent that content available on or through our site is appropriate for use or available in other locations.'
      ]
    },
    ACCOUNT_SECURITY: {
      TITLE: 'Account Security',
      PARAGRAPHS: [
        'If you are provided with a user identification code, password, or any other piece of information as part of our security procedures, you must treat such information as confidential. You must not disclose it to any third party. We have the right to disable any user identification code or password if, in our reasonable opinion, you have failed to comply with these terms of service.'
      ]
    },
    CONTENT_USAGE: {
      TITLE: 'Usage of Content on Our Site',
      PARAGRAPHS: [
        'We are the owner or licensee of all intellectual property rights in our site and in the material published on it. Those works are protected by copyright laws and treaties around the world. All rights are reserved.',
        'You may print one copy and download extracts of any page(s) from our site for your personal use and may share content with others within your organization. However, you must not modify the digital or paper copies of any materials you have printed off or downloaded, and you must not use illustrations, photographs, video, audio sequences, or graphics separately from any accompanying text.',
        'Our status (and that of any identified contributors) as authors of content on our site must always be acknowledged, except for user-generated content.',
        'You must not use any part of our content for commercial purposes without obtaining a license to do so from us or our licensors.'
      ]
    },
    NO_DATA_MINING: {
      TITLE: 'No Data Mining or Web Scraping',
      PARAGRAPHS: [
        'You shall not conduct, facilitate, authorize, or permit any text or data mining or web scraping in relation to our site or any services provided via, or in relation to, our site for any purpose, including AI development. This includes the use of automated tools such as "bots," "scrapers," or "robots" to access, copy, or monitor any part of our site.'
      ]
    },
    LINKING_TO_SITE: {
      TITLE: 'Linking to Our Site',
      PARAGRAPHS: [
        'You may link to our homepage provided you do so in a fair and legal manner that does not damage our reputation. You must not suggest any form of association, approval, or endorsement on our part where none exists. We reserve the right to withdraw linking permission without notice.',
        'For permission to link to or use content on our site beyond the allowed use, please contact us at inquiries@mqcapital.co.uk.'
      ],
      LINKS: [
        {
          TEXT: 'inquiries@mqcapital.co.uk',
          URL: 'mailto:inquiries@mqcapital.co.uk'
        }
      ]
    },
    NO_RELIANCE: {
      TITLE: 'No Reliance on Site Content',
      PARAGRAPHS: [
        'The content on our site is provided for general information only and is not intended to constitute advice on which you should rely. You should obtain professional advice before taking any action based on the content on our site.',
        'While we make reasonable efforts to update the content, we do not guarantee that the information is accurate, complete, or up-to-date.'
      ]
    },
    EXTERNAL_LINKS: {
      TITLE: 'External Links',
      PARAGRAPHS: [
        'Our site may contain links to third-party sites and resources. These links are provided for your information only and should not be interpreted as an endorsement of the linked websites or their content. We have no control over these sites or resources.'
      ]
    },
    VIRUS_PROTECTION: {
      TITLE: 'Protection Against Viruses',
      PARAGRAPHS: [
        'We do not guarantee that our site will be secure or free from bugs or viruses. It is your responsibility to configure your IT systems and use virus protection software when accessing our site.',
        'You must not introduce any viruses, trojans, worms, or other harmful material to our site. You must not attempt unauthorized access to our site or any related systems.'
      ]
    },
    LIABILITY_LIMITATIONS: {
      TITLE: 'Liability Limitations',
      LIST_ITEMS: [
        {
          TITLE: 'For business users',
          TEXT: 'We exclude all implied conditions, warranties, representations, or other terms that may apply to our site or its content.\nWe will not be liable for any loss of profits, sales, or business interruption.'
        },
        {
          TITLE: 'For consumer users',
          TEXT: 'We provide our site solely for domestic and private use. We will not be liable for any loss of profit, business interruption, or business opportunity resulting from the use of our site.\nIf any defective digital content we provide causes damage to your device, we will either repair it or provide compensation, provided that you followed our installation instructions.'
        }
      ]
    },
    PERSONAL_INFO_USAGE: {
      TITLE: 'How We Use Your Personal Information',
      PARAGRAPHS: [
        'We will use your personal information as set out in our Online Privacy Statement.'
      ]
    },
    GOVERNING_LAW: {
      TITLE: 'Governing Law and Dispute Resolution',
      PARAGRAPHS: [
        'These terms of service, their subject matter, and their formation are governed by English law. If you are a consumer, you can bring proceedings in the courts of England and Wales, or in the jurisdiction of your residence (e.g., Northern Ireland or Scotland).',
        'If you are a business, we both agree to the exclusive jurisdiction of the courts of England and Wales.'
      ]
    }
  },
  FOOTER_NOTE:
    'By using our site, you agree to be bound by these terms and conditions of use.'
};

export const RECRUITMENT_POLICY = {
  TITLE: 'Recruitment Policy Notice',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  INTRO: [
    'At MarQ Capital, we believe in treating individuals with respect and transparency. Protecting your personal information and respecting your privacy is central to our core values as a business. We are committed to complying with applicable laws and guidelines to ensure your data is safe and your privacy is protected.',
    'This Recruitment Policy Notice explains how we collect, use, and protect your personal information throughout the recruitment process. We want you to be fully informed about how we handle your data and why we collect it.',
    'Please take the time to read this notice carefully to understand how your personal information will be processed and your rights regarding the data we collect.'
  ],
  SECTIONS: {
    ROLE_AND_NOTICE: {
      TITLE: 'Our Role and When This Privacy Notice Applies',
      PARAGRAPHS: [
        'In this policy, any reference to "we," "our," "us," or "ourselves" refers to MarQ Capital and its subsidiaries (collectively, "MarQ Capital"). Any reference to "you," "your," or "yourself" refers to you as a potential candidate for employment or a services contract with MarQ Capital.',
        'For the purposes of data protection laws and this privacy notice, MarQ Capital is the data controller. This means we are responsible for determining how and why your personal information is used. Our registered office is in',
        'Level 30, The Leadenhall Building \\n122 Leadenhall Street \\nLondon EC3V 4AB UK.',
        'This privacy notice applies to the information we collect from you when you apply for a role with us or engage with our recruitment process. If you apply through our recruitment portal or any recruitment partners, your information will be passed to us for processing.'
      ]
    },
    DATA_PRINCIPLES: {
      TITLE: 'Data Protection Principles',
      INTRO:
        'We are committed to transparency and meeting our data protection obligations. According to data protection laws, we ensure that the personal information we hold about you is:',
      LIST_ITEMS: [
        { TEXT: 'Used lawfully, fairly, and transparently.' },
        {
          TEXT: 'Collected for valid purposes and not used for purposes incompatible with those.'
        },
        {
          TEXT: 'Relevant and limited to the purposes for which it was collected.'
        },
        { TEXT: 'Accurate and kept up to date.' },
        { TEXT: 'Retained only as long as necessary for the purposes stated.' },
        { TEXT: 'Kept securely.' }
      ],
      CLOSING:
        'We strive to be clear and transparent about how your personal information is used, so please read this notice carefully before interacting with us.'
    },
    PERSONAL_INFO_COLLECT: {
      TITLE: 'What Personal Information Do We Collect?',
      INTRO:
        'For recruitment and selection purposes, we collect the following information:',
      RECRUITMENT_LIST: [
        {
          TEXT: 'Personal details including your full name, title, date of birth, contact details (address, phone number, email), tax details, national insurance or social security number, identification documents, and relevant qualifications.'
        },
        {
          TEXT: 'Information from your CV, including employment history, education, skills, and qualifications.'
        },
        {
          TEXT: 'Interview feedback, results of tests, and assessments related to your suitability for the role.'
        },
        {
          TEXT: 'Communication and correspondence, including email content and metadata.'
        }
      ],
      SCREENING_INTRO:
        'For referencing and pre-employment screening purposes, we collect the following:',
      SCREENING_LIST: [
        {
          TEXT: 'References from previous employers and, where applicable, regulatory references.'
        },
        {
          TEXT: 'Immigration status, nationality, and copies of right to work documentation.'
        },
        {
          TEXT: 'Checks on criminal records, financial history, fraud, sanctions status, and other relevant background checks.'
        },
        { TEXT: 'Verification of academic qualifications.' }
      ]
    },
    INFO_SOURCE: {
      TITLE: 'Where Do We Collect Your Personal Information From?',
      PARAGRAPHS: [
        'Most of the personal information we receive is directly from you. This can include information provided during interviews, communications, meetings, or other interactions. We may also receive data from recruitment agencies, professional and social media platforms like LinkedIn, your previous employers (where you have nominated them as references), and other business partners or external agencies that assist with background checks.'
      ]
    },
    INFO_USE: {
      TITLE: 'How Will We Use Your Personal Information?',
      INTRO:
        'We will only use your personal information when the law allows us to do so. The primary purposes for which we process your data are:',
      LIST_ITEMS: [
        {
          TITLE: 'To assess your suitability',
          TEXT: 'For the role you have applied for.'
        },
        {
          TITLE: 'To comply with legal obligations',
          TEXT: 'Including employment, immigration, and anti-discrimination laws.'
        },
        {
          TITLE: 'To maintain consistent practices',
          TEXT: 'For recruitment across MarQ Capital.'
        },
        {
          TITLE: 'To contact you about future opportunities',
          TEXT: 'About future job opportunities with MarQ Capital.'
        }
      ],
      CLOSING:
        'We will rely on legitimate interests, legal obligations, or the need to perform a contract as the legal basis for processing your personal data.'
    },
    SENSITIVE_INFO: {
      TITLE: 'Sensitive Personal Information',
      PARAGRAPHS: [
        'As part of our commitment to fostering a diverse and inclusive work environment, we may ask for sensitive personal information on a voluntary basis, such as your ethnicity, gender, disability, or sexual orientation. Providing this data is optional, and there will be no consequences if you choose not to share this information.',
        'This data is used solely for internal purposes to monitor diversity, ensure fair hiring practices, and comply with equality standards. We only process this information in an aggregated and anonymized form.'
      ]
    },
    INFO_SHARING: {
      TITLE: 'Who Do We Share Your Personal Information With?',
      INTRO:
        'We may share your personal information with third parties for the following purposes:',
      LIST_ITEMS: [
        {
          TITLE: 'To facilitate the recruitment process',
          TEXT: 'Including sharing data with recruitment consultants or agencies assisting in finding suitable candidates.'
        },
        {
          TITLE: 'To meet legal and regulatory obligations',
          TEXT: 'As required by applicable laws and regulations.'
        },
        {
          TITLE: 'To service providers',
          TEXT: 'Who support our business operations, such as IT services, background check agencies, payroll providers, and other third-party contractors involved in the recruitment process.'
        }
      ],
      CLOSING:
        'We ensure that any third parties who process your data are subject to confidentiality agreements and comply with data protection laws.'
    },
    INTERNATIONAL_TRANSFERS: {
      TITLE: 'International Transfers',
      PARAGRAPHS: [
        'Some of your personal information may be transferred outside of your country of residence if, for example, our service providers are located in other countries. In such cases, we ensure that the transfer complies with applicable data protection laws, and that your information is kept secure.'
      ]
    },
    DATA_RETENTION: {
      TITLE: 'How Long Do We Keep Your Personal Information?',
      PARAGRAPHS: [
        'We retain your personal information for as long as necessary to process your application and for recruitment purposes. Typically, we will retain your information for up to 2 years after the recruitment process concludes. If you are successful in your application and join MarQ Capital, your information will become part of your employee record.'
      ]
    },
    USER_RIGHTS: {
      TITLE: 'Your Rights',
      INTRO:
        'You have a number of rights concerning the personal data we hold about you. These include:',
      LIST_ITEMS: [
        {
          TITLE: 'The right to access your personal data',
          TEXT: 'You can request a copy of the personal data we hold about you.'
        },
        {
          TITLE:
            'The right to request correction or erasure of your personal data',
          TEXT: 'You can ask us to correct inaccurate information or delete your data in certain circumstances.'
        },
        {
          TITLE: 'The right to object to the processing of your personal data',
          TEXT: 'You can object to certain types of processing, such as direct marketing.'
        },
        {
          TITLE: 'The right to withdraw consent (where applicable)',
          TEXT: 'Where we rely on your consent to process your data, you can withdraw this at any time.'
        },
        {
          TITLE: 'The right to restrict processing of your data',
          TEXT: 'You can ask us to temporarily stop processing your data while we address concerns.'
        }
      ],
      CONTACT:
        'To exercise any of these rights, please contact us at compliance@mqcapital.co.uk.',
      LINKS: [
        {
          TEXT: 'compliance@mqcapital.co.uk',
          URL: 'mailto:compliance@mqcapital.co.uk'
        }
      ]
    },
    CONTACT_US: {
      TITLE: 'Contacting Us',
      PARAGRAPHS: [
        'If you have any questions about how we process your personal information or wish to exercise any of your rights, please contact us at:',
        'MarQ Capital Limited\\nLevel 30, The Leadenhall Building 122\\nLeadenhall Street London EC3V 4AB UK\\ncompliance@mqcapital.co.uk\\n+44 20 4587 3860'
      ],
      LINKS: [
        {
          TEXT: 'compliance@mqcapital.co.uk',
          URL: 'mailto:compliance@mqcapital.co.uk'
        }
      ]
    },
    COMPLAINTS: {
      TITLE: 'Complaints',
      PARAGRAPHS: [
        'If you are dissatisfied with how we handle your personal data, you have the right to file a complaint with the relevant data protection authority. In the UK, you can contact the Financial Ombudsman Service at:',
        'Financial Ombudsman Service \\nExchange Tower Harbour Exchange Square \\nLondon E14 9SR UK \\nTel. 0845 080 1800\\n www.financial-ombudsman.org.uk'
      ],
      LINKS: [
        {
          TEXT: 'www.financial-ombudsman.org.uk',
          URL: 'https://www.financial-ombudsman.org.uk'
        }
      ]
    }
  },
  FOOTER_NOTE:
    'We are committed to handling your personal data responsibly throughout the recruitment process.'
};

export const ONLINE_PRIVACY_STATEMENT = {
  TITLE: 'Online Privacy Statement',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  INTRO: [
    'At MarQ Capital, we prioritize treating individuals and their personal information with respect, reflecting our core values as a business. We are committed to upholding the laws and guidance that protect your personal information, respecting your privacy, and keeping your data secure. We also want you to be fully informed about what we do with your personal information, which is the purpose of this privacy notice.',
    'You can find full information about how and why we use your personal information by expanding each section below.'
  ],
  RECRUITMENT_NOTE:
    'If we collect your personal information in connection with recruitment activities, you can see our privacy notice relevant to that activity here.',
  SECTIONS: {
    OUR_ROLE: {
      TITLE: 'Our Role and When This Privacy Notice Applies',
      PARAGRAPHS: [
        'In this privacy notice, any reference to "us," "we," "our," or "ourselves" refers to MarQ Capital. Any reference to "you," "your," or "yourself" refers to you as someone who has a relationship with us, has contacted us, or interacted with us in some way.',
        'For the purposes of data protection laws and this privacy notice, we are the controller of your personal information, meaning that we are responsible for deciding how we hold and use your personal information.',
        'MarQ Capital Limited\\nLevel 30, The Leadenhall Building, \\n122 Leadenhall Street, \\nLondon EC3V 4AB, UK',
        'This privacy notice applies when you interact with us or we process your personal information for any reason. For example, this notice will cover anyone who uses our website, follows our social media profiles, engages with us as a potential investor, client, or partner, or contacts us for any other reason. However, this privacy notice will not apply if you are a current or past shareholder, director, staff member, or worker for MarQ Capital.'
      ],
      ADDITIONAL_NOTES: [
        'If we collect your personal information in connection with recruitment activities, you can see our privacy notice relevant to that activity here.',
        'If you have any questions about this privacy notice, including any requests to exercise your legal rights, please contact us at inquiries@mqcapital.co.uk.'
      ],
      LINKS: [
        {
          TEXT: 'here',
          URL: '/legal/recruitment-policy-notice'
        },
        {
          TEXT: 'inquiries@mqcapital.co.uk',
          URL: 'mailto:inquiries@mqcapital.co.uk'
        }
      ]
    },
    DATA_PROTECTION: {
      TITLE: 'Data Protection Principles',
      INTRO:
        'We are committed to transparency regarding how we collect and use your personal information and to fulfilling our data protection obligations. Data protection laws require that the personal information we hold about you must be:',
      LIST_ITEMS: [
        { TEXT: 'Used lawfully, fairly, and transparently.' },
        {
          TEXT: 'Collected only for valid purposes, which we have clearly explained to you, and not used in any way incompatible with those purposes.'
        },
        {
          TEXT: 'Relevant and limited to the purposes we have told you about.'
        },
        { TEXT: 'Accurate and kept up to date.' },
        {
          TEXT: 'Kept only as long as necessary for the purposes we have outlined.'
        },
        { TEXT: 'Kept securely.' }
      ],
      CONCLUSION:
        'To ensure compliance, we are required by data protection laws to notify you of the information contained in this privacy notice. It is important that you read this document before interacting with us so that you understand how and why we process your personal information.'
    },
    PERSONAL_INFO_COLLECTION: {
      TITLE: 'What Personal Information Do We Collect?',
      INTRO:
        'In connection with your relationship or interactions with us, we may collect and process a variety of personal information, including:',
      LIST_ITEMS: [
        {
          TITLE: 'Personal Contact Details',
          TEXT: 'Name, title, address, email address, phone number(s), date of birth, age, gender, marital status, next of kin, dependents, family members, and emergency contacts.'
        },
        {
          TITLE: 'Financial Information',
          TEXT: 'Bank account details, financial transactions, and payments.'
        },
        {
          TITLE: 'Communications',
          TEXT: 'Any communications between you and us.'
        },
        {
          TITLE: 'Business Information',
          TEXT: 'Details of services carried out by us or you in connection with our relationship with you or your organization.'
        },
        {
          TITLE: 'Publicly Available Information',
          TEXT: 'Any information shared via public platforms or social media, or through public information sources such as LinkedIn.'
        },
        {
          TITLE: 'Education or Work History',
          TEXT: 'Details of your education or work history, including organizations, positions, roles, and responsibilities.'
        },
        {
          TITLE: 'Creditworthiness',
          TEXT: 'We may undertake investigations to assess whether to enter or continue a business relationship.'
        },
        {
          TITLE: 'Website Usage',
          TEXT: 'Information about how you use our website, including pages viewed, usernames, account details, IP addresses, and browser-related data.'
        },
        {
          TITLE: 'Identification Documents',
          TEXT: 'Driving license, passport, and background checks.'
        },
        {
          TITLE: 'Legal and Regulatory Information',
          TEXT: 'Any information related to queries, complaints, claims, or cases involving us and you.'
        }
      ],
      SPECIAL_CATEGORY: [
        'Additionally, with your consent, we may collect "special category data" which can include sensitive information such as health details, ethnic origin, criminal prosecutions, religious beliefs, political opinions, union membership, or sexual orientation.',
        'In some cases, regulatory requirements may make it necessary to collect this special category data to provide certain services. If this is the case, we will inform you.'
      ]
    },
    INFO_COLLECTION_METHOD: {
      TITLE: 'How We Collect Your Personal Information',
      INTRO: 'We collect your personal information from various sources:',
      LIST_ITEMS: [
        {
          TITLE: 'Directly from You',
          TEXT: 'For example, when you interact with us through email, phone, meetings, or via our website.'
        },
        {
          TITLE: 'From Organizations You Work With',
          TEXT: 'If you represent an organization that has a relationship with us, we may collect information from them.'
        },
        {
          TITLE: 'Public Sources',
          TEXT: 'Information from publicly available platforms such as LinkedIn or other online sources.'
        },
        {
          TITLE: 'Third Parties',
          TEXT: 'From service providers, credit reference agencies, legal advisors, or other professionals involved in our business relationship with you.'
        }
      ]
    },
    INFO_USAGE: {
      TITLE: 'How Will We Use Your Personal Information?',
      INTRO:
        'We use your personal information for a variety of purposes, including:',
      LIST_ITEMS: [
        {
          TITLE: 'To assess your suitability',
          TEXT: 'For any investment, business, or client-related opportunities with us.'
        },
        {
          TITLE: 'To provide services',
          TEXT: 'Or information you have requested.'
        },
        {
          TITLE: 'To meet obligations',
          TEXT: 'Legal, regulatory, or contractual obligations.'
        },
        {
          TITLE: 'To maintain and improve',
          TEXT: 'Our website and services.'
        },
        {
          TITLE: 'To manage relationships',
          TEXT: 'With current and prospective investors, clients, or partners.'
        }
      ],
      SPECIAL_CATEGORY_NOTE:
        'If we process any special category data (e.g., health, ethnicity, or criminal records), we will do so based on explicit consent, legal obligations, or other lawful grounds, as necessary.'
    },
    AUTOMATED_DECISION: {
      TITLE: 'Automated Decision-Making',
      PARAGRAPHS: [
        'We do not currently use automated decision-making that significantly affects you. Should we introduce any such process in the future, we will notify you accordingly.'
      ]
    },
    INFO_SHARING: {
      TITLE: 'Who Do We Share Your Personal Information With?',
      INTRO:
        'We may share your personal information with third parties when necessary, including:',
      LIST_ITEMS: [
        {
          TITLE: 'Service providers',
          TEXT: 'Who assist in delivering our services, such as IT providers, financial institutions, and marketing agencies.'
        },
        {
          TITLE: 'Professional advisors',
          TEXT: 'Including lawyers and auditors.'
        },
        {
          TITLE: 'Other members of MarQ Capital group',
          TEXT: 'For internal administration and reporting purposes.'
        },
        {
          TITLE: 'Legal or regulatory bodies',
          TEXT: 'If required to comply with applicable laws.'
        }
      ],
      CONCLUSION:
        'We do not share your personal information with third parties unless required to do so by law or as outlined in this privacy notice.'
    },
    INTERNATIONAL_TRANSFERS: {
      TITLE: 'International Transfers of Your Personal Information',
      PARAGRAPHS: [
        'Your personal information may be transferred outside the UK or the EEA to service providers or third parties that operate globally. We will ensure that any such transfer is compliant with applicable data protection laws and that appropriate security measures are in place.'
      ]
    },
    INFO_RETENTION: {
      TITLE: 'How Long Do We Keep Your Personal Information?',
      PARAGRAPHS: [
        'We will retain your personal information for as long as necessary to fulfill the purposes outlined in this privacy notice or to comply with legal or regulatory obligations. Generally, we retain personal information for a period of up to 6 years after the end of your relationship with us, unless specific circumstances require a longer retention period.'
      ]
    },
    USER_RIGHTS: {
      TITLE: 'Your Rights',
      INTRO:
        'You have a number of rights concerning your personal data, including:',
      LIST_ITEMS: [
        { TEXT: 'The right to access your personal data.' },
        { TEXT: 'The right to correct or erase your personal data.' },
        {
          TEXT: 'The right to object to the processing of your personal data for certain purposes, such as direct marketing.'
        },
        { TEXT: 'The right to restrict processing in certain circumstances.' },
        { TEXT: 'The right to data portability in some cases.' }
      ],
      CONTACT_INFO:
        'To exercise these rights, please contact us at inquiries@mqcapital.co.uk.',
      LINKS: [
        {
          TEXT: 'inquiries@mqcapital.co.uk',
          URL: 'mailto:inquiries@mqcapital.co.uk'
        }
      ]
    },
    NOTICE_CHANGES: {
      TITLE: 'Changes to This Notice',
      PARAGRAPHS: [
        'We may update this privacy notice from time to time. The most current version will always be available on our website. If significant changes occur, we will notify you directly or post a notice on our website.'
      ]
    }
  },
  LINKS: [
    {
      TEXT: 'here',
      URL: '/legal/recruitment-policy-notice'
    },
    {
      TEXT: 'inquiries@mqcapital.co.uk',
      URL: 'mailto:inquiries@mqcapital.co.uk'
    }
  ],
  FOOTER_NOTE:
    'We are committed to protecting your privacy and ensuring your personal information is handled responsibly.'
};
